import * as React from 'react';
// @ts-expect-error
import type {ActionMeta, SelectBaseProps} from 'react-select';

import classnames from 'classnames';

import {colors} from '../colors/colors';
import {SelectPickerGeneric} from '../select-picker-generic/select-picker-generic';
import type {
    NoOptionsMessageProps,
    FooterButtonProps,
} from '../select-picker-generic/select-picker-generic';

import {IconAndLabel} from './icon-and-label';
import type {RemoveButtonProps} from './icon-and-label';

import styles from './select-two-line-icon.module.css';

export type TwoLineOptionWithIcon<T = undefined> = {
    value: string;
    label: string;
    sublabel?: string;
    icon?: React.ComponentType<any>;
    iconNode?: React.ReactNode;
    isDisabled?: boolean;
    href?: string;
    // Useful in scenarios where you need to pass additional data with the option
    metaData?: T;
};

export type Props = SelectBaseProps & {
    value?: TwoLineOptionWithIcon<any> | null | undefined;
    options?: TwoLineOptionWithIcon<any>[];
    loadOptions?: (query: string) => Promise<TwoLineOptionWithIcon<any>[]>;
    onChange: (value: TwoLineOptionWithIcon<any>, action: ActionMeta) => void;
    // This prop makes the SelectTwoLineIcon have styles matching SelectPickerGeneric
    hasGenericStyles?: boolean;
    height?: number;
    hideDropdownIndicator?: boolean;
    shouldWrap?: boolean;
    customNoOptionsMessage?: NoOptionsMessageProps;
    footerButton?: FooterButtonProps;
    removeButton?: RemoveButtonProps;
    hasRemoveButton?: boolean;
};

export function SelectTwoLineIcon(props: Props) {
    const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        isMulti,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        isClearable,
        hasGenericStyles,
        height,
        hideDropdownIndicator,
        onBlur,
        styles: customStyles,
        shouldWrap,
        removeButton,
        hasRemoveButton,
        isSearchable = false,
        options = [],
        loadOptions,
        ...restProps
    } = props;

    const formatOptionLabel = (option: TwoLineOptionWithIcon) => {
        const isSelected = option === props.value;

        const styleName = classnames({
            [styles['select-two-line-icon-option']]: !isSelected,
            [styles['select-two-line-icon-option--selected']]: isSelected,
            [styles['select-two-line-icon-option--disabled']]: option.isDisabled,
        });

        // @ts-expect-error
        const onRemoveClick = (e) => {
            if (removeButton) {
                e.stopPropagation();
                removeButton.onClick(option.value);
            }
        };

        return (
            <div className={styleName}>
                <IconAndLabel
                    icon={option.icon}
                    iconNode={option.iconNode}
                    label={option.label}
                    sublabel={option.sublabel}
                    isSelected={isSelected}
                    shouldWrap={shouldWrap}
                    onRemoveClick={hasRemoveButton ? onRemoveClick : undefined}
                />
            </div>
        );
    };

    // @ts-expect-error
    const getBorderColor = (state) => {
        if (state.isFocused) {
            if (hasGenericStyles) {
                return `${colors.blue} !important`;
            } else {
                return colors.greyLt;
            }
        } else {
            return colors.offWhiteDk;
        }
    };

    const localStyles = {
        ...customStyles,
        // @ts-expect-error
        control: (base, state) => ({
            ...base,
            '&:hover': {
                borderColor: colors.greyLt,
            },
            borderColor: getBorderColor(state),
            borderWidth: hasGenericStyles ? '1px' : '2px',
            borderRadius: 3,
            boxShadow: 'none',
            top: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: height ? `${height}px` : '38px',
            minHeight: height ? `${height}px` : '38px',
        }),
        // @ts-expect-error
        indicatorsContainer: (base) => ({
            ...base,
            height: '100%',
        }),
        // @ts-expect-error
        dropdownIndicator: (base) => ({
            ...base,
            display: hideDropdownIndicator ? 'none' : 'flex',
        }),
    } as const;

    return (
        <div className={styles['select-two-line-icon']}>
            <SelectPickerGeneric
                {...restProps}
                onChange={props.onChange}
                options={options}
                loadOptions={loadOptions}
                styles={localStyles}
                formatOptionLabel={formatOptionLabel}
                isSearchable={isSearchable}
                // Who can explain it, who can tell why. Somehow, this
                // works. The onBlur must be explicitly set here.
                onBlur={onBlur}
            />
        </div>
    );
}
